import React, { useEffect, useMemo } from "react";
import { Popover, Avatar } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

/* ---------------- TYPES ---------------- */

type HoverCardVariant =
  | "leadSource"
  | "leadIndustry"
  | "leadStage"
  | "leadType"
  | "product"
  | "productType"
  | "customerType"
  | "dealSource"
  | "contactleadOwner"
  | "default";
 

type SubmoduleHoverCardProps = {
  name?: React.ReactNode;
  role?: string;
  leadsource_name?: string;
  leadindustry_name?: string;
  leadstage_name?: string;
  leadtype_name?: string;
  product_name?: string;
  producttype_name?: string;
  customertype_name?: string;
  dealsource_name?: string;
  contactleadowner_name?: string;
  company_name?: string;
  createdAt?: string;
  email?: string;        
  phone?: string;
  price?: number | string;
  currency?: "INR" | "USD";

  /** Controls behavior per page */
  variant?: HoverCardVariant;
};

/* ---------------- DATE FORMATTER ---------------- */

const formatDateTime = (dateStr: string) => {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  const time = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) return `Today, ${time}`;
  if (isYesterday) return `Yesterday, ${time}`;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatPrice = (
  value?: number | string,
  currency: "INR" | "USD" = "INR"
) => {
  if (value === undefined || value === null || value === "") return "";

  const number =
    typeof value === "string" ? Number(value.replace(/,/g, "")) : value;

  if (isNaN(Number(number))) return "";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    currencyDisplay: "code", // INR / USD
    maximumFractionDigits: 0,
  }).format(Number(number));
};



/* ---------------- COMPONENT ---------------- */

const SubmoduleHoverCard = ({
  name,
  role,
  leadsource_name,
  leadindustry_name,
  leadstage_name,
  leadtype_name,
  product_name,
  price,
  currency,
  producttype_name,
  customertype_name,
  dealsource_name,
  contactleadowner_name,
  company_name,
  email,         
  phone,
  createdAt,
  variant = "default",
}: SubmoduleHoverCardProps) => {
  const navigate = useNavigate();

  /* ---------- Inject shine animation once ---------- */
  useEffect(() => {
    const styleId = "hover-card-shine";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        @keyframes shine {
          0% { transform: skewX(-20deg) translateX(-150%); }
          50% { transform: skewX(-20deg) translateX(150%); }
          100% { transform: skewX(-20deg) translateX(150%); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  /* ---------- Avatar Initial ---------- */
  const initial = useMemo(
    () => (typeof name === "string" ? name.charAt(0).toUpperCase() : ""),
    [name]
  );

  /* ---------- Date ---------- */
  const formattedDate = useMemo(
    () => (createdAt ? formatDateTime(createdAt) : ""),
    [createdAt]
  );

  const formattedPrice = useMemo(
  () => formatPrice(price, currency),
  [price, currency]
);



  /* ---------- Variant Based Navigation ---------- */
  const config = useMemo(() => {
    switch (variant) {
      case "leadSource":
        return {
          onNavigate: () =>
            navigate(
              `/leads?source=${encodeURIComponent(leadsource_name || "")}`
            ),
          buttonText: "View Leads for this Lead Source →",
          titleHint: "View leads for this source",
        };

      case "leadIndustry":
        return {
          onNavigate: () =>
            navigate(
              `/leads?industry=${encodeURIComponent(leadindustry_name || "")}`
            ),
          buttonText: "View Leads for this Lead Industry →",
          titleHint: "View leads for this Lead industry",
        };

      case "leadStage":
        return {
          onNavigate: () =>
            navigate(
              `/leads?stage=${encodeURIComponent(leadstage_name || "")}`
            ),
          buttonText: "View Leads for this Lead Stage →",
          titleHint: "View leads for this Lead Stage",
        };
      case "leadType":
        return {
          onNavigate: () =>
            navigate(
              `/leads?type=${encodeURIComponent(leadtype_name || "")}`
            ),
          buttonText: "View Leads for this Lead Type →",
          titleHint: "View leads for this Lead Type",
        };
      case "product":
        return {
          onNavigate: () =>
            navigate(
              `/leads?product=${encodeURIComponent(product_name || "")}`
            ),
          buttonText: "View Leads for this Products →",
          titleHint: "View leads for this Products",
        };
      case "productType":
        return {
          onNavigate: () =>
            navigate(
              `/leads?producttype=${encodeURIComponent(producttype_name || "")}`
            ),
          buttonText: "View Leads for this Product Type →",
          titleHint: "View leads for this Product Type",
        };
      case "customerType":
        return {
          onNavigate: () =>
            navigate(
              `/leads?customertype=${encodeURIComponent(customertype_name || "")}`
            ),
          buttonText: "View Leads for this Customer Potential →",
          titleHint: "View leads for this Customer Potential",
        };
      case "dealSource":
        return {
          onNavigate: () =>
            navigate(
              `/crm/deals?dealsource=${encodeURIComponent(dealsource_name || "")}`
            ),
          buttonText: "View Deals for this Deal Source →",
          titleHint: "View Deals for this Deal Source",
        };
      case "contactleadOwner":
        return {
          onNavigate: () =>
            navigate(
              `/crm/contact-list?contactleadowner=${encodeURIComponent(
        contactleadowner_name || ""
      )}&company=${encodeURIComponent(company_name || "")}`
            ),
          buttonText: "View Contacts for this Lead Owner →",
          titleHint: "View Contacts for this Lead Owner",
        };

      default:
        return {
          onNavigate: () => {},
          buttonText: "View Details →",
          titleHint: "",
        };
    }
  }, [variant,
     navigate,
    leadsource_name,
    leadindustry_name,
    leadstage_name, 
    leadtype_name,
    product_name,
    producttype_name,
  dealsource_name ]);

  /* ---------- Card Content ---------- */
  const content = (
    <div style={{ width: 260 }}>
      {/* Header */}
      <div className="d-flex align-items-center mb-2">
        <Avatar
          shape="square"
          className="btn btn-primary"
          style={{ width: 36, height: 36, fontWeight: 600 }}
        >
          {initial || <UserOutlined />}
        </Avatar>

        <div className="ms-2">
          <div
            className="fw-semibold text-decoration-underline text-truncate"
            style={{
    cursor: "pointer",
    maxWidth: 180,     // 👈 control truncation width
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }}
            onClick={(e) => {
              e.stopPropagation();
              config.onNavigate();
            }}
            title={typeof name === "string" ? name : undefined}
          >
            {name || "Unassigned"}
          </div>
          <div className="text-muted small">{role || "-"}</div>
        </div>
      </div>

      {/* Price */}
{formattedPrice && (
  <div className="d-flex align-items-center mb-1 small">
    <span className="fw-semibold">{formattedPrice}</span>
  </div>
)}

{email && (
  <div className="d-flex align-items-center small mb-1">
    <i className="ti ti-mail me-2" />
    <span className="text-truncate" style={{ maxWidth: 200 }}>
      {email}
    </span>
  </div>
)}

{/* Phone */}
{phone && (
  <div className="d-flex align-items-center small mb-1">
    <i className="ti ti-phone me-2" />
    <span>{phone}</span>
  </div>
)}


      {/* Date */}
      {formattedDate && (
        <div className="d-flex align-items-center mb-1 small">
          <i className="ti ti-calendar-time me-2" />
          {formattedDate}
        </div>
      )}

      {/* Action Button */}
      {variant !== "default" && (
        <button
          className="btn btn-sm btn-outline-primary w-100 mt-2"
          onClick={(e) => {
            e.stopPropagation();
            config.onNavigate();
          }}
          style={{ position: "relative", overflow: "hidden" }}
        >
          <span style={{ position: "relative", zIndex: 2 }}>
            {config.buttonText}
          </span>
          <span
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to right, transparent 0%, rgba(255,255,255,.4) 50%, transparent 100%)",
              transform: "skewX(-20deg) translateX(-150%)",
              animation: "shine 3s infinite",
            }}
          />
        </button>
      )}
    </div>
  );

  /* ---------- Trigger ---------- */
  return (
    <Popover content={content} placement="right" trigger="hover">
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer" }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === "Enter" && config.onNavigate()}
      >
        {name || "Unassigned"}
      </span>
    </Popover>
  );
};

export default SubmoduleHoverCard;
